/**
 * @file ModalContext.js
 * @description Fornece um contexto global para exibir modais de alerta e confirmação.
 */
import React, { createContext, useState, useContext, useCallback } from "react";
import Modal from "../components/Modal";

const ModalContext = createContext();

export const useModal = () => useContext(ModalContext);

export const ModalProvider = ({ children }) => {
  const [modalState, setModalState] = useState({
    isOpen: false,
    title: "",
    message: "",
    type: "info", // 'info', 'success', 'error', 'confirm'
    onConfirm: () => {},
    onCancel: () => {},
  });

  // Usamos uma referência para armazenar as funções de resolução da Promise
  const resolver = React.useRef();

  const showAlert = useCallback(({ title, message, type = "info" }) => {
    setModalState({
      isOpen: true,
      title,
      message,
      type,
      onConfirm: null, // Não há confirmação num alerta simples
    });
  }, []);

  const showConfirm = useCallback(({ title, message }) => {
    return new Promise((resolve) => {
      setModalState({
        isOpen: true,
        title,
        message,
        type: "confirm",
      });
      // Armazena a função resolve para ser usada nos botões
      resolver.current = resolve;
    });
  }, []);

  const handleClose = () => {
    if (modalState.type === "confirm" && resolver.current) {
      resolver.current(false); // Resolve a promise como 'false' se o utilizador cancelar
    }
    setModalState({ ...modalState, isOpen: false });
  };

  const handleConfirm = () => {
    if (modalState.type === "confirm" && resolver.current) {
      resolver.current(true); // Resolve a promise como 'true'
    }
    setModalState({ ...modalState, isOpen: false });
  };

  const value = { showAlert, showConfirm };

  return (
    <ModalContext.Provider value={value}>
      {children}
      <Modal
        isOpen={modalState.isOpen}
        onClose={handleClose}
        onConfirm={handleConfirm}
        title={modalState.title}
        message={modalState.message}
        type={modalState.type}
      />
    </ModalContext.Provider>
  );
};
